import CriterionRows from '@Components/Search/SearchWithCriteria/CriteriaRows'
import Button from '@Components/Buttons/Button'
import { ICONS } from '@Const/common'
import React, { JSX, useEffect } from 'react'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { observer } from 'mobx-react'
import clsx from 'clsx'

interface SearchCriteriaSection<T extends object> {
    searchCriteriaStore: SearchCriteriaStore<T>
    canAddCriteria?: boolean
    isUniqueTooltip?: JSX.Element
    orientation: 'column' | 'row'
    disableAutoComplete?: boolean
}

const SearchCriteriaSection = <T extends object>(props: SearchCriteriaSection<T>) => {
    const { searchCriteriaStore, canAddCriteria, orientation, isUniqueTooltip, disableAutoComplete } = props
    const { formState } = useFormContext<{ search: Array<SearchCriterion<T>> }>()
    const { fields, replace } = useFieldArray<{ search: Array<SearchCriterion<T>> }, 'search', 'fieldId'>({
        name: 'search',
        keyName: 'fieldId',
    })

    // Sync criterion values back to the store
    const watch = useWatch<{ search: Array<SearchCriterion<T>> }>({ name: 'search' })

    useEffect(() => {
        if (formState.isDirty) {
            searchCriteriaStore.syncFormValues(watch as Array<SearchCriterion<T>>)
        }
    }, [formState, watch, searchCriteriaStore])

    const storeCriteria = searchCriteriaStore.searchCriteria

    useEffect(() => {
        const hasStructuralChanges =
            fields.length !== storeCriteria.length ||
            fields.some((field, index) => field.id !== storeCriteria[index]?.id)

        if (hasStructuralChanges) {
            replace(storeCriteria)
        }
    }, [fields, replace, storeCriteria])

    return (
        <div className={clsx(orientation === 'row' && 'search-side-by-side')}>
            <CriterionRows
                searchCriteriaStore={searchCriteriaStore}
                fields={fields}
                disableAutoComplete={disableAutoComplete}
            />
            {canAddCriteria && (
                <Button
                    mainColor
                    className="add-criteria-button"
                    type="secondary"
                    submitType="button"
                    icon={ICONS.add}
                    disabled={searchCriteriaStore.isAddCriterionDisabled}
                    onClick={() => searchCriteriaStore.addCriterion()}
                >
                    {searchCriteriaStore.atMaxCriteria ? 'Max Criteria Reached' : 'Add Criteria'}
                    {searchCriteriaStore.isUniqueSelected && isUniqueTooltip}
                </Button>
            )}
        </div>
    )
}

export default observer(SearchCriteriaSection)
